import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

export type TonoCalendario = "programada" | "manual" | "enviada";

const TONOS: Record<TonoCalendario, { bg: string; borde: string; texto: string }> = {
  /** Envío automático, todavía no salió — teal (design #1399, decisión 6). */
  programada: { bg: "bg-primary-100", borde: "border-primary", texto: "text-primary-strong" },
  /** Envío manual que quedó `programada` (falló al disparar) — navy. */
  manual: { bg: "bg-navy-bg", borde: "border-foreground", texto: "text-foreground" },
  /** Ya salió a terreno — gris, deja de llamar la atención. */
  enviada: { bg: "bg-muted", borde: "border-border-strong", texto: "text-muted-foreground" },
};

const ETIQUETA_TONO: Record<TonoCalendario, string> = {
  programada: "Programada",
  manual: "Envío manual",
  enviada: "Ya enviada",
};

const DIAS_SEMANA = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];

function mismoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function tituloMes(mes: Date): string {
  const texto = mes.toLocaleDateString("es-CL", { month: "long", year: "numeric" });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

export interface CalendarioMensualProps<T> {
  /** Cualquier fecha dentro del mes que se está mostrando. */
  mes: Date;
  onCambiarMes: (mes: Date) => void;
  /** TODOS los bloques (de cualquier mes) — el calendario filtra los del mes visible. */
  bloques: T[];
  getFecha: (bloque: T) => Date;
  /** Contenido interno del bloque (título + subtítulo). El color de texto lo
   * pone el contenedor según `tono`; un `<span>` sin color propio lo hereda. */
  renderBloque: (bloque: T) => React.ReactNode;
  tono: (bloque: T) => TonoCalendario;
  /** Aclaración corta junto a la navegación de mes (ej. "Cada bloque es una
   * temática que va a salir. Todavía no existe cuestionario."). */
  leyenda?: React.ReactNode;
  /** Se muestra en vez de la grilla cuando `bloques` está vacío. */
  vacio?: React.ReactNode;
  className?: string;
}

/**
 * Calendario mensual del Design System (design #1399, decisión 6) — grilla de
 * 7 columnas propia, NO `ui/calendar` (react-day-picker es un date-picker, no
 * un lienzo de contenido agendado). Usado por "Plan de envíos" (E4): cada
 * celda es un día, cada bloque una temática con `fecha_programada`.
 */
export function CalendarioMensual<T>({
  mes,
  onCambiarMes,
  bloques,
  getFecha,
  renderBloque,
  tono,
  leyenda,
  vacio,
  className,
}: CalendarioMensualProps<T>) {
  if (bloques.length === 0 && vacio) {
    return <div className={cn("border-border-subtle rounded-xl border", className)}>{vacio}</div>;
  }

  const anio = mes.getFullYear();
  const mesIdx = mes.getMonth();
  const primerDia = new Date(anio, mesIdx, 1);
  const diasEnMes = new Date(anio, mesIdx + 1, 0).getDate();
  // Lunes = 0 ... Domingo = 6 (getDay() nativo es Domingo = 0).
  const offset = (primerDia.getDay() + 6) % 7;

  const bloquesDelMes = bloques.filter((b) => {
    const f = getFecha(b);
    return f.getFullYear() === anio && f.getMonth() === mesIdx;
  });

  const celdas: { dia: number | null; bloques: T[] }[] = [];
  for (let i = 0; i < 42; i++) {
    const dia = i - offset + 1;
    const dentro = dia >= 1 && dia <= diasEnMes;
    if (i >= 35 && !dentro) continue;
    celdas.push({
      dia: dentro ? dia : null,
      bloques: dentro
        ? bloquesDelMes.filter((b) => getFecha(b).getDate() === dia)
        : [],
    });
  }

  const mesAnterior = () => onCambiarMes(new Date(anio, mesIdx - 1, 1));
  const mesSiguiente = () => onCambiarMes(new Date(anio, mesIdx + 1, 1));

  // La leyenda nombra SÓLO los tonos que aparecen en los datos. Antes listaba
  // los tres siempre: una empresa sin ningún envío manual igual leía "Envío
  // manual" junto a un color que no iba a encontrar en ninguna celda. Se
  // calcula sobre `bloques` completo, no sobre el mes visible, para que la
  // leyenda no cambie al pasar de mes.
  const tonosPresentes = (Object.keys(TONOS) as TonoCalendario[]).filter((t) =>
    bloques.some((b) => tono(b) === t)
  );

  return (
    <div className={cn("bg-card border-border-subtle overflow-x-auto rounded-xl border", className)}>
      <div className="border-border-subtle flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <span className="text-[14.5px] font-semibold whitespace-nowrap">{tituloMes(mes)}</span>
        <div className="flex gap-0.5">
          <button
            type="button"
            aria-label="Mes anterior"
            onClick={mesAnterior}
            className="border-input hover:bg-border-faint text-muted-foreground flex size-7 items-center justify-center rounded-md border transition-colors"
          >
            <Icon name="chevron_left" size={17} />
          </button>
          <button
            type="button"
            aria-label="Mes siguiente"
            onClick={mesSiguiente}
            className="border-input hover:bg-border-faint text-muted-foreground flex size-7 items-center justify-center rounded-md border transition-colors"
          >
            <Icon name="chevron_right" size={17} />
          </button>
        </div>
        {leyenda && <span className="text-soft text-[12px] whitespace-nowrap">{leyenda}</span>}
        <div className="flex-1" />
        <div className="flex flex-wrap gap-3.5">
          {tonosPresentes.map((t) => (
            <div key={t} className="text-soft flex items-center gap-1.5 text-[11.5px] whitespace-nowrap">
              <span className={cn("size-[7px] rounded-xs", TONOS[t].bg, "border", TONOS[t].borde)} />
              {ETIQUETA_TONO[t]}
            </div>
          ))}
        </div>
      </div>

      <div className="border-border-subtle grid grid-cols-7 border-b" style={{ minWidth: 900 }}>
        {DIAS_SEMANA.map((d) => (
          <div key={d} className="text-soft px-2.5 py-2 text-[11px] font-semibold tracking-wide">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7" style={{ minWidth: 900 }}>
        {celdas.map((celda, i) => (
          <div
            key={i}
            className={cn(
              "border-border-faint flex min-h-[104px] flex-col gap-1 border-r border-b px-2 py-[7px]",
              celda.dia === null && "bg-surface-muted/60"
            )}
          >
            {celda.dia !== null && (
              <span
                className={cn(
                  "fc-num text-[11px]",
                  hoyEs(anio, mesIdx, celda.dia) ? "text-primary-hover font-semibold" : "text-muted-foreground"
                )}
              >
                {celda.dia}
              </span>
            )}
            {celda.bloques.map((b, j) => {
              const t = TONOS[tono(b)];
              return (
                <div
                  key={j}
                  className={cn(
                    "flex flex-col gap-px rounded-md border-l-2 px-1.5 py-1",
                    t.bg,
                    t.borde,
                    t.texto
                  )}
                >
                  {renderBloque(b)}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function hoyEs(anio: number, mesIdx: number, dia: number): boolean {
  const hoy = new Date();
  return mismoDia(hoy, new Date(anio, mesIdx, dia));
}
